import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TransactionHistory = ({ transactions, selectedCurrency }) => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const formatCurrency = (amount) => {
    const symbols = { USD: '$', EUR: '€', BTC: '₿', ETH: 'Ξ' };
    const decimals = selectedCurrency === 'BTC' || selectedCurrency === 'ETH' ? 6 : 2;
    return `${symbols?.[selectedCurrency]}${amount?.toLocaleString('en-US', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    })}`;
  };

  const formatQuantity = (quantity) => {
    return quantity?.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 8 
    });
  };

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'buy': return 'text-success';
      case 'sell': return 'text-error';
      case 'transfer': return 'text-accent';
      default: return 'text-muted-foreground';
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'buy': return 'ArrowDownCircle';
      case 'sell': return 'ArrowUpCircle';
      case 'transfer': return 'ArrowRightLeft';
      default: return 'Circle';
    }
  };

  const filteredTransactions = transactions?.filter(transaction => {
    const matchesFilter = filter === 'all' || transaction?.type?.toLowerCase() === filter;
    const matchesSearch = transaction?.asset?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         transaction?.symbol?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'buy', label: 'Buy' },
    { key: 'sell', label: 'Sell' },
    { key: 'transfer', label: 'Transfer' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
          <h3 className="text-lg font-semibold text-foreground">Transaction History</h3>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <Input
              type="search"
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full sm:w-64"
            />
            <div className="flex space-x-1">
              {filters?.map((f) => (
                <Button
                  key={f?.key}
                  variant={filter === f?.key ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setFilter(f?.key)}
                >
                  {f?.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Date</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Type</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Asset</th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">Quantity</th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">Price</th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">Total</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions?.map((transaction) => (
              <tr key={transaction?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {new Date(transaction.date)?.toLocaleDateString()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(transaction.date)?.toLocaleTimeString()}
                    </p>
                  </div>
                </td>
                <td className="p-4">
                  <div className={`flex items-center space-x-2 ${getTypeColor(transaction?.type)}`}>
                    <Icon name={getTypeIcon(transaction?.type)} size={16} />
                    <span className="font-medium capitalize">{transaction?.type}</span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <Image
                      src={transaction?.icon}
                      alt={transaction?.asset}
                      className="w-6 h-6 rounded-full"
                    />
                    <div>
                      <p className="font-medium text-foreground">{transaction?.asset}</p>
                      <p className="text-sm text-muted-foreground">{transaction?.symbol}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-right">
                  <p className="font-medium text-foreground">
                    {transaction?.type === 'sell' ? '-' : '+'}{formatQuantity(transaction?.quantity)}
                  </p>
                </td>
                <td className="p-4 text-right">
                  <p className="font-medium text-foreground">{formatCurrency(transaction?.price)}</p>
                </td>
                <td className="p-4 text-right">
                  <p className="font-medium text-foreground">{formatCurrency(transaction?.total)}</p>
                </td>
                <td className="p-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    transaction?.status === 'completed' 
                      ? 'bg-success/20 text-success' 
                      : transaction?.status === 'pending' ?'bg-warning/20 text-warning' :'bg-error/20 text-error'
                  }`}>
                    {transaction?.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="md:hidden space-y-4 p-4">
        {filteredTransactions?.map((transaction) => (
          <div key={transaction?.id} className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <Image
                  src={transaction?.icon}
                  alt={transaction?.asset}
                  className="w-8 h-8 rounded-full"
                />
                <div>
                  <p className="font-medium text-foreground">{transaction?.asset}</p>
                  <p className="text-sm text-muted-foreground">{transaction?.symbol}</p>
                </div>
              </div>
              <div className={`flex items-center space-x-2 ${getTypeColor(transaction?.type)}`}>
                <Icon name={getTypeIcon(transaction?.type)} size={16} />
                <span className="font-medium capitalize">{transaction?.type}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm mb-3">
              <div>
                <p className="text-muted-foreground">Quantity</p>
                <p className="font-medium text-foreground">
                  {transaction?.type === 'sell' ? '-' : '+'}{formatQuantity(transaction?.quantity)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Price</p>
                <p className="font-medium text-foreground">{formatCurrency(transaction?.price)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Total</p>
                <p className="font-medium text-foreground">{formatCurrency(transaction?.total)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Status</p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  transaction?.status === 'completed' 
                    ? 'bg-success/20 text-success' 
                    : transaction?.status === 'pending' ?'bg-warning/20 text-warning' :'bg-error/20 text-error'
                }`}>
                  {transaction?.status}
                </span>
              </div>
            </div>
            
            <div className="text-xs text-muted-foreground">
              {new Date(transaction.date)?.toLocaleString()}
            </div>
          </div>
        ))}
      </div>
      {filteredTransactions?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No transactions found matching your criteria.</p>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;